/**
 * 투표 결과 화면의 콘텐츠 데이터.
 * 출처: _workspace/05_design-analyst_spec-result.md (§1, §1-7, §9-7)
 *
 * - 랭킹은 노드 이름 인덱스가 아니라 득표수 데이터로 정렬한다 (§9-7).
 * - 이미지 일부는 투표 화면(menu-vote)과 동일 해시 → 기존 에셋 재사용.
 */

import { RESULT_HERO_DECORATIONS, SHARED_HERO_DECORATIONS } from './decorations';

const RESULT_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/menu-result`;
const VOTE_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/menu-vote`;

/** 순위별 rate 배지 색상 키 (CSS class 매핑용) */
export type RankTier = 'winner' | 'second' | 'third' | 'fourth';

export interface RankEntry {
  id: string;
  /** 메뉴명 */
  name: string;
  /** 득표율 (%) — 배지 표기 */
  ratePercent: number;
  /** 득표수 — 정렬 기준 + 표기 */
  voteCount: number;
  /** 썸네일 이미지 경로 (없으면 fallback 배경) */
  thumb: string | null;
  /** 순위 아이콘(메달/트로피) 이미지 경로. 없으면 숫자(rank) 표기 */
  icon: string | null;
}

/** 우승(1위) 메뉴 — 우승 카드 + 통계 */
export const WINNER = {
  id: 'menu-sogogi',
  name: '소고기삼겹 된장전골',
  /** 우승 카드 사진 — 투표 화면 menu-03(소고기, hash 743ffcb7)와 동일 → 재사용 */
  image: `${VOTE_BASE}/menu-03-sogogi.png`,
  imageSize: { width: 346, height: 434 },
  ratePercent: 38,
  voteCount: 18365,
  /** 우승 메뉴 여부 — 공유 entity 의 isAnswer 필드에 매핑(배지 UI 는 미구현) */
  isAnswer: true,
};

/**
 * 2·3·4위 랭킹 (dim 리스트).
 * 정렬은 voteCount 내림차순 (§9-7). 노드 이름 인덱스(result-01/02/03)와 무관.
 * - 2위 우렁: hash e927e556 = menu-vote menu-01-woorong → 재사용
 * - 3위 모둠수육: hash ac894c83 = menu-vote menu-02-pyogo(동일 사진) → 재사용
 * - 4위 바지락: 신규 → menu-result/rank-thumb-bajirak.png
 */
export const RANKING: RankEntry[] = [
  {
    id: 'rank-woorong',
    name: '우렁 된장전골',
    ratePercent: 27,
    voteCount: 13049,
    thumb: `${VOTE_BASE}/menu-01-woorong.png`,
    icon: `${RESULT_BASE}/rank-icon-2nd.png`,
  },
  {
    id: 'rank-modeum',
    name: '모둠 수육전골',
    ratePercent: 20,
    voteCount: 9666,
    thumb: `${VOTE_BASE}/menu-02-pyogo.png`,
    icon: `${RESULT_BASE}/rank-icon-3rd.png`,
  },
  {
    id: 'rank-bajirak',
    name: '바지락 된장전골',
    ratePercent: 15,
    voteCount: 7249,
    thumb: `${RESULT_BASE}/rank-thumb-bajirak.png`,
    icon: null, // 4위는 숫자("4") 표기
  },
];

/** 결과 식당 정보 — 평점/거리/설명 포함 (투표 화면보다 정보 많음) */
export const RESULT_RESTAURANT = {
  name: '옥된장',
  distance: '식당·200m',
  description: '된장전골과 牛수육 전문점',
  logo: `${VOTE_BASE}/restaurant-logo.png`,
  logoSize: { width: 62, height: 40 },
  /** 평점 — Figma 101:1614 단일 별(회색) */
  rating: 0,
  ratingMax: 1,
};

/** 결과 타이틀 (2줄) */
export const RESULT_TITLE = {
  line1: '내가 선택한 메뉴가',
  line2: '1위!',
};

/** 리워드 CTA 라벨 (§1-5, §9-1 오타 "흭득"→"획득" 교정) */
export const REWARD_CTA = {
  pointLabel: 'P',
  pointValue: '20',
  message: '게이지 리워드 획득',
};

const RESULT_ASSET_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/menu-result`;

/** 랭킹 하단 프로모 배너 — Figma 136:10960 */
export const RESULT_PROMO_BANNER = {
  image: `${RESULT_ASSET_BASE}/banner.png`,
  subtitlePlain: '응원하기 좋은',
  subtitleAccent: ' 선선한 밤',
  titlePlain: '가장 먹고 싶은',
  titleAccent: '치킨 메뉴',
  titleSuffix: '는?',
  stars: `${RESULT_ASSET_BASE}/promo-banner-stars.png`,
  deco: `${RESULT_ASSET_BASE}/promo-banner-deco.png`,
  soccer: `${RESULT_ASSET_BASE}/promo-banner-soccer.png`,
} as const;

/** 결과 화면 장식 이미지 */
export const RESULT_DECORATIONS = {
  ...SHARED_HERO_DECORATIONS,
  ...RESULT_HERO_DECORATIONS,
  trophy: `${RESULT_BASE}/trophy.png`,
  coin: `${RESULT_BASE}/coin.png`,
};
